import React, { startTransition, useEffect, useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Container from 'reactstrap/lib/Container';
import Row from 'reactstrap/lib/Row';
import Col from 'reactstrap/lib/Col';
import { get } from 'lodash-es';
import classNames from 'classnames';

/* Models */
import { VisitorEntities, VisitorModel } from 'client/data/models/visitor';
import { VehicleModel } from 'client/data/models/vehicle-v2';
import {
  DealershipsModel,
  buildDealershipsPath,
  buildNationalDealershipsPath,
  PAGE_SIZE,
} from 'client/data/models/dealerships';

/* Utils */
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { getBaseBreadcrumb, getHomeBreadcrumb } from 'site-modules/shared/components/breadcrumbs/helper';
import { venomHistory } from 'client/utils/history/venom-history';
import { TrackingHandler } from 'client/tracking/handler';
import { isUnknownZip } from 'site-modules/shared/utils/location';
import { getDealershipsLandingTitle } from 'client/site-modules/dealerships/utils/seo-utils';
import { heroMask } from 'site-modules/shared/components/clipped-container/mask-library/dealerships-landing-hero';
import { objectToQueryString } from 'site-modules/shared/utils/string';

/* Constants */
import { PAGE_DISPLAY_NAME } from 'site-modules/dealerships/constants/dealerships-landing';
import { TrackingMap } from 'client/tracking/maps/dealerships/dealerships-landing';

/* Components */
import { Breadcrumbs } from 'site-modules/shared/components/breadcrumbs/breadcrumbs';
import { AdUnit } from 'client/site-modules/shared/components/ad-unit/ad-unit';
import { SuggestedVehiclePricingCarouselContainer } from 'site-modules/shared/components/suggested-vehicle-pricing-carousel/suggested-vehicle-pricing-carousel-container';
import { MakeZipSelection } from 'site-modules/dealerships/components/make-zip-selection/make-zip-selection';
import { CommonPagination } from 'site-modules/shared/components/common-pagination/common-pagination';
import { DealersCardsList } from 'site-modules/dealerships/components/dealers-cards-list/dealers-cards-list';
import { AboutDealerReviewsRatings } from 'site-modules/dealerships/components/about-dealer-reviews-ratings/about-dealer-reviews-ratings';
import { MinHeightWrapper } from 'site-modules/shared/components/min-height-wrapper/min-height-wrapper';
import { ClippedContainer } from 'site-modules/shared/components/clipped-container/clipped-container';
import { LoadingOverlay } from 'site-modules/shared/components/loading-spinner/loading-overlay';

import './dealerships-landing.scss';

const DEFAULT_PAGE_NUM = '1';

export function DealershipsLandingUI({
  visitorLocation = {},
  makeName: makeNameProp = undefined,
  make: makeSlug = null,
  pageNum = DEFAULT_PAGE_NUM,
  dealershipsSearchResults: dealershipsSearchResultsProp = undefined,
  location = {},
  isMobile = false,
}) {
  const [selectedMake, setSelectedMake] = useState(makeSlug);
  const [isLoading, setIsLoading] = useState(false);
  const [dealershipsSearchResults, setDealershipsSearchResults] = useState(dealershipsSearchResultsProp);
  const [makeName, setMakeName] = useState(makeNameProp);

  useEffect(() => {
    startTransition(() => setIsLoading(!dealershipsSearchResultsProp));

    if (dealershipsSearchResultsProp) {
      startTransition(() => {
        setDealershipsSearchResults(dealershipsSearchResultsProp);
        setMakeName(makeNameProp);
      });
    }
  }, [dealershipsSearchResultsProp, makeNameProp]);

  useEffect(() => {
    TrackingHandler.useMap(TrackingMap);
  }, []);

  const buildPageLink = useCallback(
    pageNumValue => {
      const query = objectToQueryString({ make: selectedMake, pagenumber: pageNumValue });
      return `${location.pathname}?${query}`;
    },
    [location.pathname, selectedMake]
  );

  const onGoBtnClick = useCallback(() => {
    if (selectedMake !== makeSlug) {
      venomHistory.push(buildPageLink(DEFAULT_PAGE_NUM));
    }
  }, [buildPageLink, makeSlug, selectedMake]);

  const handleSelectedMakeChange = useCallback(updatedMake => setSelectedMake(updatedMake.slug), []);

  const { city, stateCode } = visitorLocation;
  const isDefaultView = !makeSlug;
  const breadcrumbs = [getHomeBreadcrumb(), getBaseBreadcrumb(PAGE_DISPLAY_NAME, location.pathname)];
  const totalPages = dealershipsSearchResults?.totalPages;
  const totalNumber = dealershipsSearchResults?.totalNumber;
  const results = dealershipsSearchResults?.results;
  const useSimpleTitle = isDefaultView || isUnknownZip(visitorLocation);
  const title = getDealershipsLandingTitle({ totalNumber, makeName, useSimpleTitle, city, stateCode });

  return (
    <main id="main-content" className="dealerships-landing bg-cool-gray-90 pos-r">
      <LoadingOverlay className={{ 'd-none': !isLoading }} />
      <ClippedContainer containerClasses="hero-gradient pos-a top-0 left-0 w-100" mask={heroMask} />
      <Container className="pb-3">
        <Breadcrumbs className="ps-0 py-0 my-2 text-white" olClassName="pb-0" data={breadcrumbs} />
        <h1 className={classNames('text-white fw-bold mb-2', { 'heading-2': isMobile, 'heading-1': !isMobile })}>
          {title}
        </h1>
        <Row>
          <Col xs={12} lg={8}>
            <MakeZipSelection
              selectedMake={selectedMake}
              onSelectedMakeChange={handleSelectedMakeChange}
              onGoBtnClick={onGoBtnClick}
            />
          </Col>
        </Row>
        {isDefaultView && (
          <Row className="mt-1 mt-md-1_5">
            <Col xs={12} lg={8}>
              <AboutDealerReviewsRatings isMobile={isMobile} />
            </Col>
          </Row>
        )}
        <Row className="mt-1">
          <Col xs={12} lg={8} className="mb-1 mb-lg-0">
            {totalNumber === 0 ? (
              'Sorry, we could not find any matching dealerships near you. Please check back later.'
            ) : (
              <DealersCardsList dealers={results} isMobile={isMobile} />
            )}
            <CommonPagination
              paginationLinkBuilder={buildPageLink}
              pageNumber={+pageNum}
              pagesCount={totalPages}
              pageResultsCount={results?.length}
              pageSize={PAGE_SIZE}
              totalCount={totalNumber}
              isMobile={isMobile}
              showNoPagesCopy={false}
            />
          </Col>
          <Col
            xs={12}
            lg={4}
            className="d-flex flex-row flex-lg-column justify-content-center justify-content-lg-start"
          >
            <AdUnit adName="mrect" xs sm md lg xl position={'1'} renderWhenViewable={false} noRefresh />
            <div className="my-auto">
              <AdUnit adName="mrect" md lg xl position={'2'} renderWhenViewable={false} noRefresh />
            </div>
          </Col>
        </Row>
        {isDefaultView && (
          <>
            <hr className="mt-1_5 mb-2" />
            <MinHeightWrapper className="suggested-pricing-wrapper" componentType="SUGGESTED_PRICING_CAROUSEL">
              <SuggestedVehiclePricingCarouselContainer className="px-0" showSeparator={false} />
            </MinHeightWrapper>
          </>
        )}
      </Container>
    </main>
  );
}

export const mapStateToProps = state => ({
  isMobile: state.mobile,
});
export const stateToPropsConfig = {
  visitorLocation: bindToPath('location', VisitorModel),
  dealershipsSearchResults: bindToPath(
    ({ make, pageNum }) => (make ? buildDealershipsPath({ make, pageNum }) : buildNationalDealershipsPath({ pageNum })),
    DealershipsModel,
    null,
    false
  ),
  makeName: bindToPath('makes', VehicleModel, (makes, { make }) => (make ? get(makes, `${make}.name`, '') : '')),
};

export const DealershipsLanding = connect(mapStateToProps)(connectToModel(DealershipsLandingUI, stateToPropsConfig));

DealershipsLandingUI.propTypes = {
  visitorLocation: VisitorEntities.Location,
  dealershipsSearchResults: PropTypes.shape({}),
  makeName: PropTypes.string,
  make: PropTypes.string,
  pageNum: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  location: PropTypes.shape({}),
  isMobile: PropTypes.bool,
};
